import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import Newsletter from "@/components/Newsletter";
import { useSiteMedia } from "@/context/SiteMediaContext";
const VIDEO_START_OFFSETS = [0, 0, 5];
const Home = () => {
  const { t, tl, lang } = useLang();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [families, setFamilies] = useState([]);
  const [activeVideo, setActiveVideo] = useState(0);
  const videoRefs = useRef([]);
  const { media } = useSiteMedia();
  const homeVideos = [media("home_video_1"), media("home_video_2"), media("home_video_3")].filter(Boolean);
  const videoSignature = homeVideos.join("|");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/products").then((r) => setFeatured(r.data.filter((_, i) => [0, 7, 12, 4].includes(i))));
    api.get("/olfactory-families").then((r) => setFamilies(r.data));
  }, [lang]);

  useEffect(() => {
    const pendingStarts = [];
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === activeVideo) {
        const startVideo = () => {
          const offset = VIDEO_START_OFFSETS[index] || 0;
          video.currentTime = Number.isFinite(video.duration)
            ? Math.min(offset, Math.max(0, video.duration - 0.1))
            : offset;
          video.play().catch(() => undefined);
        };
        if (video.readyState >= 1) {
          startVideo();
        } else {
          video.addEventListener("loadedmetadata", startVideo, { once: true });
          pendingStarts.push([video, startVideo]);
        }
      } else {
        video.pause();
      }
    });
    return () => pendingStarts.forEach(([video, handler]) => video.removeEventListener("loadedmetadata", handler));
  }, [activeVideo, videoSignature]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-[85vh]">
        <div className="flex flex-col justify-center px-6 md:px-12 lg:px-20 py-20 order-2 lg:order-1">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-champagne mb-6">{t("hero.eyebrow")}</p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-charcoal">
              {t("hero.title1")}
              <br />
              <span className="italic">{t("hero.title2")}</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-base text-stone-500 leading-relaxed mt-8 max-w-md">{t("hero.subtitle")}</p>
          </Reveal>
          <Reveal delay={360}>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="group inline-flex items-center gap-2 bg-charcoal text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-800 transition-colors"
              >
                {t("hero.cta")}
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 border-b border-stone-400 pb-1 text-xs uppercase tracking-[0.2em] text-stone-600 hover:border-charcoal hover:text-charcoal transition-colors"
              >
                {t("hero.cta2")}
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="relative order-1 lg:order-2 min-h-[45vh] lg:min-h-full">
          {homeVideos.map((video, index) => (
            <video
              key={video}
              ref={(element) => { videoRefs.current[index] = element; }}
              data-testid={index === activeVideo ? "hero-video" : undefined}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${index === activeVideo ? "opacity-100 z-[1]" : "opacity-0"}`}
              src={video}
              muted
              playsInline
              preload="auto"
              onEnded={() => setActiveVideo((index + 1) % homeVideos.length)}
              aria-hidden={index !== activeVideo}
            />
          ))}
          {homeVideos.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center gap-2" aria-label="Selezione video">
              {homeVideos.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveVideo(index)}
                  aria-label={`Video ${index + 1}`}
                  aria-current={activeVideo === index}
                  className={`h-1 transition-[width,background-color] duration-300 ${
                    activeVideo === index ? "w-10 bg-white" : "w-5 bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <div className="max-w-xl mb-16">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-charcoal">{t("home.categories.title")}</h2>
            <p className="text-stone-500 mt-4">{t("home.categories.subtitle")}</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((c, i) => (
            <Reveal key={c.slug} delay={i * 120}>
              <Link
                to={`/shop?cat=${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group block overflow-hidden bg-white border border-stone-200"
              >
                <div className="aspect-[4/5] overflow-hidden bg-stone-100">
                  <img
                    src={media(`home_category_${c.slug.replaceAll("-", "_")}`) || c.image}
                    alt={tl(c.name)}
                    className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${c.slug === "profumi" ? "object-[58%_center]" : "object-center"}`}
                  />
                </div>
                <div className="p-7 bg-[#f7f3ee] min-h-[150px]">
                  <h3 className="font-serif text-2xl md:text-3xl text-charcoal">{tl(c.name)}</h3>
                  <p className="text-stone-600 text-sm mt-2 max-w-[260px]">{tl(c.tagline)}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <Reveal>
          <div className="flex items-end justify-between mb-14">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-charcoal">{t("home.featured.title")}</h2>
              <p className="text-stone-500 mt-4">{t("home.featured.subtitle")}</p>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-600 hover:text-charcoal border-b border-stone-400 hover:border-charcoal pb-1 transition-colors">
              {t("nav.shop")} <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 100}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Brand story */}
      <section className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
        <div className="relative min-h-[50vh]">
          <img src={media("home_story")} alt="Immagine storia Kaós" className="absolute inset-0 w-full h-full object-cover object-center" />
        </div>
        <div className="bg-champagne-soft flex flex-col justify-center px-6 md:px-16 py-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-stone-600 mb-6">{t("home.story.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-charcoal whitespace-pre-line leading-tight">
              {t("home.story.title")}
            </h2>
            <p className="text-stone-600 leading-relaxed mt-8 max-w-md">{t("home.story.body")}</p>
            <p className="font-serif italic text-3xl text-charcoal mt-7 -rotate-2">Marika</p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-10 text-xs uppercase tracking-[0.2em] text-charcoal border-b border-charcoal pb-1 hover:gap-3 transition-all"
              data-testid="story-cta"
            >
              {t("home.story.cta")} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Inclusive skincare */}
      <section className="grid grid-cols-1 md:grid-cols-2 bg-[#eee7de]">
        <div className="min-h-[460px] md:min-h-[620px]">
          <img src={media("home_inclusive")} alt={t("home.inclusive.imageAlt")} className="h-full w-full object-cover object-center" loading="lazy" />
        </div>
        <div className="flex items-center px-6 py-16 md:px-14 lg:px-20">
          <Reveal>
            <p className="text-[10px] uppercase tracking-[0.25em] text-stone-500">{t("home.inclusive.eyebrow")}</p>
            <h2 className="mt-5 max-w-lg font-serif text-4xl md:text-5xl leading-tight text-charcoal">{t("home.inclusive.title")}</h2>
            <p className="mt-6 max-w-md leading-relaxed text-stone-600">{t("home.inclusive.body")}</p>
            <Link to="/shop?cat=skincare" className="mt-9 inline-flex items-center gap-2 border-b border-charcoal pb-1 text-xs uppercase tracking-[0.18em]">
              {t("home.inclusive.cta")} <ArrowRight size={14}/>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Olfactory families */}
      {families.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 border-t border-stone-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-5">
            {families.map((f, i) => (
              <Reveal key={f.slug} delay={i * 60}>
                <Link
                  to={`/shop?cat=profumi&family=${f.slug}`}
                  data-testid={`family-chip-${i}`}
                  aria-label={tl(f.name)}
                  className="group block aspect-[4/5] overflow-hidden bg-stone-100"
                >
                  <img
                    src={media(`family_${String(i + 1).padStart(2, "0")}`)}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <Newsletter />
    </div>
  );
};

export default Home;
